import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Camera, CheckCircle, Loader2 } from 'lucide-react';
import { ITEM_STATES } from './fleetUtils';

const PHOTO_REQUIRED_CATS = ['pneus_roues', 'carrosserie', 'equipements_securite'];

const PhotoPicker = ({ photoIds, onAdd }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/fleet/photos`, fd, { withCredentials: true });
      onAdd(res.data.photo_id);
    } catch (e) {
      alert(e.response?.data?.detail || 'Échec envoi photo');
    } finally {
      setUploading(false);
    }
  };
  return (
    <div className="flex items-center gap-2">
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
        onChange={(e) => upload(e.target.files?.[0])} />
      <Button type="button" size="sm" variant="outline" disabled={uploading}
        onClick={() => inputRef.current?.click()} data-testid="checklist-photo-upload-btn">
        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4 mr-1" />}
        Photo ({(photoIds || []).length})
      </Button>
    </div>
  );
};

const ChecklistDialog = ({ vehicle, open, onClose, onValidated }) => {
  const [template, setTemplate] = useState(null);
  const [inspection, setInspection] = useState(null);
  const [results, setResults] = useState({});
  const [mileage, setMileage] = useState('');
  const [openCats, setOpenCats] = useState({});
  const [justification, setJustification] = useState('');
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [draftStatus, setDraftStatus] = useState('');
  const saveTimer = useRef(null);

  useEffect(() => {
    if (!open || !vehicle) return;
    (async () => {
      try {
        const [tplRes, inspRes] = await Promise.all([
          axios.get(`${API}/fleet/template`, { withCredentials: true }),
          axios.post(`${API}/fleet/inspections`, { vehicle_id: vehicle.vehicle_id }, { withCredentials: true })
        ]);
        setTemplate(tplRes.data);
        setInspection(inspRes.data);
        setResults(inspRes.data.results || {});
        setMileage(inspRes.data.mileage ?? '');
      } catch (e) {
        alert(e.response?.data?.detail || 'Impossible de démarrer le contrôle');
        onClose();
      }
    })();
  }, [open, vehicle]);

  const scheduleSave = useCallback((newResults, newMileage) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    setDraftStatus('Enregistrement...');
    saveTimer.current = setTimeout(async () => {
      try {
        setSaving(true);
        await axios.put(`${API}/fleet/inspections/${inspection.inspection_id}`,
          { results: newResults, mileage: newMileage === '' ? null : Number(newMileage) },
          { withCredentials: true });
        setDraftStatus('Brouillon enregistré ✓');
      } catch {
        setDraftStatus('Échec enregistrement brouillon');
      } finally {
        setSaving(false);
      }
    }, 800);
  }, [inspection]);

  const setItem = (itemKey, patch) => {
    const next = { ...results, [itemKey]: { ...(results[itemKey] || {}), ...patch } };
    setResults(next);
    scheduleSave(next, mileage);
  };

  const allConformeCat = (cat) => {
    const next = { ...results };
    cat.items.forEach((it) => {
      const cur = next[it.key] || {};
      next[it.key] = { ...cur, state: 'conforme' };
    });
    setResults(next);
    scheduleSave(next, mileage);
  };

  const handleValidate = async () => {
    setValidating(true);
    try {
      const res = await axios.post(`${API}/fleet/inspections/${inspection.inspection_id}/validate`,
        { uncontrolled_justification: justification || null }, { withCredentials: true });
      alert(`Contrôle validé. ${res.data.issues_created.length} anomalie(s) créée(s).` +
        (res.data.blocking_alerts > 0 ? ` ⚠️ ${res.data.blocking_alerts} défaut(s) BLOQUANT(S) : le chef de parc a été alerté.` : ''));
      onValidated?.();
      onClose();
    } catch (e) {
      alert(e.response?.data?.detail || 'Validation refusée');
    } finally {
      setValidating(false);
    }
  };

  const setOpenSingle = (key) => setOpenCats(p => (p[key] ? {} : { [key]: true }));

  if (!open) return null;
  const uncontrolledCount = template
    ? template.categories.flatMap(c => c.items).filter(it => !(results[it.key]?.state) || results[it.key]?.state === 'non_controle').length
    : 0;
  const doneCats = template ? template.categories.filter(cat => cat.items.every(it => results[it.key]?.state && results[it.key].state !== 'non_controle')).length : 0;
  const problemItems = template ? template.categories.flatMap(c => c.items).filter(it => ['a_surveiller', 'defectueux', 'bloquant'].includes(results[it.key]?.state)) : [];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="checklist-dialog">
        <DialogHeader>
          <DialogTitle>Contrôle hebdomadaire — {vehicle?.plate_number}</DialogTitle>
        </DialogHeader>
        {!template || !inspection ? (
          <div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-500">{draftStatus || 'Brouillon enregistré automatiquement pendant la saisie.'}</span>
              <span className="font-semibold text-blue-700" data-testid="checklist-progress">{doneCats} catégorie(s) sur {template.categories.length} terminée(s)</span>
            </div>
            {template.categories.map((cat) => {
              const isOpen = !!openCats[cat.key];
              const catDone = cat.items.every(it => results[it.key]?.state && results[it.key].state !== 'non_controle');
              const catProblem = cat.items.some(it => ['a_surveiller', 'defectueux', 'bloquant'].includes(results[it.key]?.state));
              return (
                <div key={cat.key} className={`border rounded-lg ${catProblem ? 'border-orange-400' : catDone ? 'border-green-400' : 'border-gray-200'}`}>
                  <div className="flex items-center justify-between p-3 cursor-pointer"
                    onClick={() => setOpenSingle(cat.key)}
                    data-testid={`checklist-cat-${cat.key}`}>
                    <span className="font-semibold text-sm">
                      {catDone ? '✅ ' : catProblem ? '⚠️ ' : ''}{cat.label}
                    </span>
                    <Button type="button" size="sm" variant="outline"
                      onClick={(e) => { e.stopPropagation(); allConformeCat(cat); }}
                      data-testid={`cat-all-ok-${cat.key}`}>
                      <CheckCircle className="w-4 h-4 mr-1 text-green-600" /> Tout est conforme
                    </Button>
                  </div>
                  {isOpen && (
                    <div className="px-3 pb-3 space-y-3">
                      {cat.items.map((it) => {
                        const r = results[it.key] || {};
                        const isProblem = ['a_surveiller', 'defectueux', 'bloquant'].includes(r.state);
                        const photoNeeded = it.photo_required_on_issue || PHOTO_REQUIRED_CATS.includes(cat.key);
                        return (
                          <div key={it.key} className="border-t pt-2">
                            <p className="text-sm font-medium mb-1">{it.label}</p>
                            <div className="flex flex-wrap gap-1">
                              {ITEM_STATES.map(s => (
                                <button key={s.value} type="button"
                                  onClick={() => setItem(it.key, { state: s.value })}
                                  className={`text-xs px-2 py-1 rounded transition-colors ${r.state === s.value ? s.cls : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                  data-testid={`item-${it.key}-${s.value}`}>
                                  {s.label}
                                </button>
                              ))}
                            </div>
                            {isProblem && (
                              <div className="mt-2 space-y-2 bg-orange-50 p-2 rounded">
                                <Textarea rows={2} placeholder="Description du problème (obligatoire)"
                                  value={r.description || ''}
                                  onChange={(e) => setItem(it.key, { description: e.target.value })}
                                  data-testid={`item-${it.key}-description`} />
                                <div className="flex flex-wrap items-center gap-3">
                                  <PhotoPicker photoIds={r.photo_ids}
                                    onAdd={(pid) => setItem(it.key, { photo_ids: [...(r.photo_ids || []), pid] })} />
                                  {photoNeeded && <span className="text-xs text-red-600 font-medium">Photo obligatoire</span>}
                                  <label className="flex items-center gap-1 text-xs">
                                    <input type="checkbox" checked={!!r.immobilization_recommended}
                                      onChange={(e) => setItem(it.key, { immobilization_recommended: e.target.checked })} />
                                    Immobilisation recommandée
                                  </label>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
            <div>
              <Label>Kilométrage constaté</Label>
              <Input type="number" value={mileage}
                onChange={(e) => { setMileage(e.target.value); scheduleSave(results, e.target.value); }}
                placeholder="ex : 84500" data-testid="checklist-mileage" />
            </div>
            {uncontrolledCount > 0 && (
              <div className="bg-yellow-50 border border-yellow-300 rounded p-3">
                <p className="text-sm font-medium text-yellow-800 mb-1">
                  {uncontrolledCount} élément(s) « Non contrôlé » — justification obligatoire pour valider :
                </p>
                <Textarea rows={2} value={justification} onChange={(e) => setJustification(e.target.value)}
                  placeholder="Pourquoi ces éléments n'ont pas été contrôlés ?" data-testid="checklist-justification" />
              </div>
            )}
            <div className="bg-gray-50 border rounded p-3 text-sm" data-testid="checklist-summary">
              <p className="font-semibold mb-1">Résumé avant validation</p>
              <p>{doneCats}/{template.categories.length} catégories terminées · {problemItems.length} anomalie(s) déclarée(s) · {uncontrolledCount} élément(s) non contrôlé(s)</p>
              {problemItems.length > 0 && (
                <ul className="list-disc ml-4 text-xs text-orange-700 mt-1">
                  {problemItems.map(it => <li key={it.key}>{it.label} — {results[it.key]?.state}</li>)}
                </ul>
              )}
            </div>
            <Button className="w-full" onClick={handleValidate} disabled={validating || saving}
              data-testid="checklist-validate-btn">
              {validating ? 'Validation...' : 'Valider définitivement le contrôle'}
            </Button>
            <p className="text-xs text-gray-400 text-center">Une checklist validée n'est plus modifiable.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ChecklistDialog;
